import {
  Marker,
  type MarkerShape,
  px,
} from "@/components/tool-kit/charts/marks";
import {
  CHART_FONT_SIZE,
  LEGEND_SWATCH,
  type LegendLayout,
} from "@/lib/tool-kit/charts/layout";
import { CHART_INK } from "@/lib/tool-kit/palette";

export interface LegendKey {
  color: string;
  /** A filled square (bars, slices), a point marker, or a short line with a marker. */
  kind: "swatch" | "marker" | "line";
  shape?: MarkerShape;
  /** An outline marker — the box plot's outlier symbol. */
  hollow?: boolean;
}

/**
 * A legend drawn inside the image, where APA 7 puts it ("within the borders of the
 * figure"), so it travels with the exported file. Positions come from `layoutLegend`;
 * `keys[i]` draws entry `i`. Text is ink, never the series colour — a light palette
 * colour is unreadable as text, and identity comes from the key beside it.
 */
export function SvgLegend({
  layout,
  keys,
  x,
  y,
}: {
  layout: LegendLayout;
  keys: readonly LegendKey[];
  /** Left edge of the area the legend was laid out in. */
  x: number;
  y: number;
}) {
  const fontSize = CHART_FONT_SIZE.legend;
  return (
    <g>
      {layout.items.map((item) => {
        const key = keys[item.index];
        if (!key) {
          return null;
        }
        const left = x + item.x;
        const middle = y + item.y + layout.rowHeight / 2;
        return (
          <g key={item.index}>
            <LegendSymbol
              cx={left + LEGEND_SWATCH / 2}
              cy={middle}
              entry={key}
            />
            <text
              fill={CHART_INK.text}
              fontSize={fontSize}
              x={px(left + LEGEND_SWATCH + 6)}
              y={px(middle + fontSize * 0.35)}
            >
              {item.label}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function LegendSymbol({
  entry,
  cx,
  cy,
}: {
  entry: LegendKey;
  cx: number;
  cy: number;
}) {
  const half = LEGEND_SWATCH / 2;
  if (entry.kind === "swatch") {
    return (
      <rect
        fill={entry.color}
        height={LEGEND_SWATCH}
        width={LEGEND_SWATCH}
        x={px(cx - half)}
        y={px(cy - half)}
      />
    );
  }
  return (
    <g>
      {entry.kind === "line" ? (
        <line
          stroke={entry.color}
          strokeWidth={2}
          x1={px(cx - half - 2)}
          x2={px(cx + half + 2)}
          y1={px(cy)}
          y2={px(cy)}
        />
      ) : null}
      <Marker
        fill={entry.color}
        hollow={entry.hollow}
        shape={entry.shape ?? "circle"}
        size={9}
        x={cx}
        y={cy}
      />
    </g>
  );
}
